import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { confirmations as confirmApi, bookings as bookingsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Phone, CheckCircle, XCircle, Clock, AlertTriangle, ArrowLeft, Hotel, Send } from 'lucide-react';
import { toast } from 'sonner';

function ConfirmDialog({ booking, onConfirmed }) {
  const [last4, setLast4] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (last4.length !== 4) { toast.error('Enter exactly 4 digits'); return; }
    setLoading(true);
    try {
      await bookingsApi.confirmPhone(booking.id, { credit_card_last4: last4 });
      toast.success('Booking confirmed!');
      setOpen(false);
      setLast4('');
      onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200" data-testid="confirm-phone-button">
          <CheckCircle className="w-3 h-3 mr-1" /> Confirm
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="heading-font">Confirm Booking</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Guest: <strong>{booking.guest_name}</strong> ({booking.guest_email})</p>
        <p className="text-sm text-muted-foreground">Enter the last 4 digits of the guest's credit card to confirm:</p>
        <Input value={last4} onChange={e => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
               placeholder="1234" maxLength={4} className="text-center text-2xl tracking-widest mono" data-testid="confirm-last4-input" />
        <Button onClick={handleConfirm} disabled={loading || last4.length !== 4} className="w-full" data-testid="confirm-submit">
          {loading ? 'Confirming...' : 'Confirm Booking'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ booking, onCancelled }) {
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    try {
      await bookingsApi.cancel(booking.id, { reason: reason || 'Guest did not confirm', cancelled_by: 'hotel' });
      toast.success('Booking cancelled');
      setOpen(false);
      onCancelled();
    } catch (e) {
      toast.error('Failed to cancel');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-red-600 border-red-200">
          <XCircle className="w-3 h-3 mr-1" /> Cancel
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="heading-font">Cancel Booking</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Guest: <strong>{booking.guest_name}</strong></p>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for cancellation (optional)" />
        <Button variant="destructive" onClick={handleCancel} disabled={loading} className="w-full">
          {loading ? 'Cancelling...' : 'Cancel Booking'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ConfirmationsPage() {
  const { hotelId } = useParams();
  const [bookingsList, setBookingsList] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await confirmApi.list(hotelId, filter || undefined);
      setBookingsList(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [hotelId, filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="confirmations-page">
      <nav className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Phone className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
            <span className="font-bold heading-font">Payment Confirmations</span>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-2 mb-6">
          {[{v: '', l: 'All'}, {v: 'pending', l: 'Pending'}, {v: 'confirmed', l: 'Confirmed'}, {v: 'cancelled', l: 'Cancelled'}].map(({v, l}) => (
            <Button key={v} variant={filter === v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(v)}>{l}</Button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-8">Loading...</p>
        ) : bookingsList.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No pay-at-property bookings found.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookingsList.map(b => (
                    <TableRow key={b.id} className={isOverdue(b.confirmation_deadline) && b.payment_status === 'pending' ? 'bg-red-50' : ''}>
                      <TableCell className="font-medium">{b.guest_name}</TableCell>
                      <TableCell className="text-sm">{b.guest_email}</TableCell>
                      <TableCell className="text-sm">{b.check_in} → {b.check_out}</TableCell>
                      <TableCell className="mono font-semibold">${b.final_price}</TableCell>
                      <TableCell>
                        {b.confirmation_deadline ? (
                          <div className="flex items-center gap-1 text-xs">
                            {isOverdue(b.confirmation_deadline) && b.payment_status === 'pending' ? (
                              <><AlertTriangle className="w-3 h-3 text-red-500" /><span className="text-red-500">Overdue</span></>
                            ) : (
                              <><Clock className="w-3 h-3 text-amber-500" /><span>{new Date(b.confirmation_deadline).toLocaleDateString()}</span></>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {b.payment_status === 'confirmed' ? (
                          <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Confirmed</Badge>
                        ) : b.booking_status === 'cancelled' ? (
                          <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>
                        ) : (
                          <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>
                        )}
                        {b.credit_card_last4 && <span className="text-xs text-muted-foreground ml-2 mono">****{b.credit_card_last4}</span>}
                      </TableCell>
                      <TableCell>
                        {b.payment_status === 'pending' && b.booking_status !== 'cancelled' && (
                          <div className="flex gap-1">
                            <ConfirmDialog booking={b} onConfirmed={loadData} />
                            <CancelDialog booking={b} onCancelled={loadData} />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
